import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import apiClient from '../../Api/apiClient';
import { PHOTO_BASE_URL } from '../../Api/config';
import Contact from '../../Components/Contact/Contact';
import LocationInpc from '../../Components/Location/LocationInpc';

const VerifyProfile = () => {
  // State Initialization
  const [nidPreview, setNidPreview] = useState(null);
  const [nomineePreview, setNomineePreview] = useState(null);
  const [otherDocsPreview, setOtherDocsPreview] = useState(null);
  const [profileId, setProfileId] = useState('');
  const [verified, setVerified] = useState(false);

  const [profileData, setProfileData] = useState({
    full_name: '',
    nid_certificate: null,
    nominee_nid: null,
    others_documents: null,
  });

  const [selectedLocation, setSelectedLocation] = useState({
    districtId: null,
    districtName: '',
    upazilaId: null,
    upazilaName: '',
  });

  // Fetch Profile Data on Component Mount
  useEffect(() => {
    const fetchAffiliatorVerifyData = async () => {
      try {
        const response = await apiClient.get('/affiliator/profile');
        const data = response.data.data;
        setProfileId(data.id);


        console.log(data)
        if (data.account_verified === 1) {
          setVerified(true)
        } else {
          setVerified(false)
        }


        setProfileData({
          full_name: data.full_name || '',
          district_id: data.district_id || null,
          upazila_id: data.upazila_id || null,
          nid_certificate: data.nid_certificate
            ? `${PHOTO_BASE_URL}${data.nid_certificate}`
            : null,
          nominee_nid: data.nominee_nid
            ? `${PHOTO_BASE_URL}${data.nominee_nid}`
            : null,
          others_documents: data.others_documents
            ? `${PHOTO_BASE_URL}${data.others_documents}`
            : null,
        });

        // Update selected location
        setSelectedLocation({
          districtId: data.district_id || null,
          districtName: data.district || '',
          upazilaId: data.upazila_id || null,
          upazilaName: data.upazila || '',
        });

        // Set preview images
        setNidPreview(
          data.nid_certificate
            ? `${PHOTO_BASE_URL}${data.nid_certificate}`
            : null
        );
        setNomineePreview(
          data.nominee_nid ? `${PHOTO_BASE_URL}${data.nominee_nid}` : null
        );
        setOtherDocsPreview(
          data.others_documents
            ? `${PHOTO_BASE_URL}${data.others_documents}`
            : null
        );
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchAffiliatorVerifyData();
  }, []);

  // Handle Input Change
  const handleInputChange = e => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  // Handle Location Selection
  const handleLocationSelect = (
    districtId,
    districtName,
    upazilaId,
    upazilaName
  ) => {
    setSelectedLocation({ districtId, districtName, upazilaId, upazilaName });
    setProfileData(prev => ({
      ...prev,
      district_id: districtId,
      upazila_id: upazilaId,
    }));
  };

  // Handle File Upload
  const handleFileUpload = (file, name) => {
    if (!file) return;
    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      Swal.fire({
        title: 'Not Accept!',
        text: 'Please upload a valid Image.',
        icon: 'error',
        customClass: {
          popup: 'w-72 h-auto p-3',
          title: 'text-sm',
          content: 'text-xs',
          confirmButton:
            'bg-DefaultColor text-white px-4 py-1 text-sm rounded-md',
        },
      });
      return;
    }
    // Create object URL for preview
    const imageURL = URL.createObjectURL(file);

    // Set preview state based on file type
    if (name === 'nid_certificate') setNidPreview(imageURL);
    else if (name === 'nominee_nid') setNomineePreview(imageURL);
    else if (name === 'others_documents') setOtherDocsPreview(imageURL);

    // Update profileData state
    setProfileData(prev => ({ ...prev, [name]: file }));
  };

  // Handle File Drop
  const handleFileDrop = (e, name) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileUpload(file, name);
  };

  // Handle File Input
  const handleFileInput = (e, name) => {
    const file = e.target.files[0];
    handleFileUpload(file, name);
  };

  // Handle Save Click
  const handleSaveClick = async e => {
    e.preventDefault();

    if (!profileId) {
      Swal.fire({
        title: 'Error!',
        text: 'Profile ID missing. Try reloading.',
        icon: 'error',
        confirmButtonText: 'Okay',
        customClass: {
          popup: 'w-72 h-auto p-3',
          title: 'text-sm',
          content: 'text-xs',
          confirmButton:
            'bg-DefaultColor text-white px-4 py-1 text-sm rounded-md',
        },
      });
      return;
    }

    const formData = new FormData();

    // Append fields to FormData
    if (profileData.full_name)
      formData.append('full_name', profileData.full_name);
    if (profileData.nid_certificate)
      formData.append('nid_certificate', profileData.nid_certificate);
    if (profileData.nominee_nid)
      formData.append('nominee_nid', profileData.nominee_nid);
    if (profileData.others_documents)
      formData.append('others_documents', profileData.others_documents);
    if (selectedLocation.districtId)
      formData.append('district_id', selectedLocation.districtId);
    if (selectedLocation.upazilaId)
      formData.append('upazila_id', selectedLocation.upazilaId);

    // Debugging: Log FormData entries
    for (const [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }

    try {
      const response = await apiClient.patch(
        '/affiliator/profile/verification',
        formData,

        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      console.log('Verify response:', response.data);

      Swal.fire({
        title: 'Success!',
        text: 'Profile verified successfully.',
        icon: 'success',
        confirmButtonText: 'Okay',
        customClass: {
          popup: 'w-72 h-auto p-3',
          title: 'text-sm',
          content: 'text-xs',
          confirmButton:
            'bg-DefaultColor text-white px-4 py-1 text-sm rounded-md',
        },
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      let errorMessage = 'Something went wrong!';
      if (error.response && error.response.data) {
        errorMessage = Object.values(error.response.data).flat().join('\n');
      }

      Swal.fire({
        title: 'Update Failed!',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'Okay',
        customClass: {
          popup: 'w-72 h-auto p-3',
          title: 'text-sm',
          content: 'text-xs',
          confirmButton:
            'bg-DefaultColor text-white px-4 py-1 text-sm rounded-md',
        },
      });
    }
  };

  // Render File Upload Section
  const renderFileUpload = (name, label, required) => (
    <div
      key={name}
      className="flex flex-col md:flex-row items-center gap-3"
      onDrop={e => handleFileDrop(e, name)}
      onDragOver={e => e.preventDefault()}
    >
      <label
        className="md:w-1/3 w-full text-profileUpdateTextColor font-medium"
        htmlFor={name}
      >
        {label}
        {/* {required && <span className="text-red-500">*</span>} */}
      </label>
      <div className="bg-white rounded-md p-1 w-full cursor-pointer border border-gray-300">
        <input
          type="file"
          accept="image/*"
          name={name}
          id={name}
          onChange={e => handleFileInput(e, name)}
          className="hidden"
          required={required}
        />
        <label
          htmlFor={name}
          className="p-2 border-dashed border-2 border-gray-300 rounded text-center block"
        >
          <p>Drag & Drop or Click to Upload {label}</p>
          {/* {profileData[name] && (
            <p className="text-sm text-gray-600 mt-2">{profileData[name]}</p>
          )} */}
        </label>
      </div>
    </div>
  );

  // Render Preview Section
  const renderPreview = (preview, label) => (
    <div className="w-1/3">
      <p className="font-semibold">{label}</p>
      {preview ? (
        <img
          src={preview}
          alt={`${label} Preview`}
          className="w-full h-40 object-cover mx-auto rounded-lg border"
        />
      ) : (
        <div className="w-full h-40 flex items-center justify-center bg-gray-100 rounded-lg border">
          <p className="text-gray-500">No {label} uploaded</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="flex items-center gap-5 flex-col lg:flex-col justify-evenly md:px-20 px-2 md:py-24 md:mt-20 mt-24 py-10 md:text-xs text-xs max-w-5xl mx-auto">
        <h1 className="font-bold text-xl">Verify your profile</h1>
        {/* Left Side: Drag and Drop Form */}
        <div className="w-full md:mb-0 mb-4">
          <form
            onSubmit={handleSaveClick}
            className="space-y-4 md:p-6 p-3 bg-ProfileUpdateColor bg-opacity-40 rounded-lg shadow-md"
          >
            {/* Name Input */}
            <div className="flex flex-col md:flex-row items-center gap-3">
              <label className="md:w-1/3 w-full text-profileUpdateTextColor font-medium">
                Name
                {/* <span className="text-red-500">*</span> */}
              </label>
              <input
                type="text"
                name="full_name"
                value={profileData.full_name}
                placeholder="Enter your name"
                onChange={handleInputChange}
                required
                className="w-full ml-1 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Location Input */}
            <LocationInpc
              onSelectLocation={handleLocationSelect}
              selectedLocation={selectedLocation}
            />

            {/* File Upload Sections */}
            {[
              { name: 'nid_certificate', label: 'NID', required: true },
              { name: 'nominee_nid', label: 'Nominee NID', required: true },
              {
                name: 'others_documents',
                label: 'Other Documents',
                required: true,
              },
            ].map(({ name, label, required }) =>
              renderFileUpload(name, label, required)
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              {verified?(
                <></>
              ):(
                <button
                  type="submit"
                  className="bg-DefaultColor text-white px-4 py-2 text-sm rounded-md"
                >
                  Verify
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Right Side: Preview Section */}
        <div className="w-full p-2 rounded text-center flex justify-center items-center space-x-4">
          {renderPreview(nidPreview, 'NID')}
          {renderPreview(nomineePreview, 'Nominee NID')}
          {renderPreview(otherDocsPreview, 'Other Documents')}
        </div>
      </div>
      <Contact />
    </>
  );
};

export default VerifyProfile;



// import React, { useState, useEffect } from 'react';
// import Swal from 'sweetalert2';
// import apiClient from '../../Api/apiClient';
// import { PHOTO_BASE_URL } from '../../Api/config';
// import Contact from '../../Components/Contact/Contact';
// import LocationInpc from '../../Components/Location/LocationInpc';

// const VerifyProfile = () => {
//   const [nidPreview, setNidPreview] = useState(null);
//   const [nomineePreview, setNomineePreview] = useState(null);
//   const [otherDocsPreview, setOtherDocsPreview] = useState(null);
//   const [profileId, setProfileId] = useState('');

//   const [fileNames, setFileNames] = useState({
//     nid_certificate: '',
//     nominee_nid: '',
//     others_documents: '',
//   });

//   const [profileData, setProfileData] = useState({
//     full_name: '',
//     nid_certificate: null,
//     nominee_nid: null,
//     others_documents: null,
//   });

//   // Handle input change
//   const handleInputChange = e => {
//     const { name, value } = e.target;
//     setProfileData({
//       ...profileData,
//       [name]: value,
//     });
//   };

//   const [selectedLocation, setSelectedLocation] = useState({
//     districtId: null,
//     districtName: '',
//     upazilaId: null,
//     upazilaName: '',
//   });

//   // Handle location selection
//   const handleLocationSelect = (
//     districtId,
//     districtName,
//     upazilaId,
//     upazilaName
//   ) => {
//     setSelectedLocation({ districtId, districtName, upazilaId, upazilaName });
//     setProfileData(prev => ({
//       ...prev,
//       district_id: districtId,
//       upazila_id: upazilaId,
//     }));
//   };

//   useEffect(() => {
//     const fetchAffiliatorVerifyData = async () => {
//       try {
//         const response = await apiClient.get('/affiliator/profile');
//         const data = response.data.data;
//         setProfileId(data.id);

//         // Store previous uploads
//         setProfileData({
//           district_id: response.data.data.district_id || null,
//           upazila_id: response.data.data.upazila_id || null,

//           nid_certificate: data.nid_certificate
//             ? `${PHOTO_BASE_URL}${data.nid_certificate}`
//             : null,
//           nominee_nid: data.nominee_nid
//             ? `${PHOTO_BASE_URL}${data.nominee_nid}`
//             : null,
//           others_documents: data.others_documents
//             ? `${PHOTO_BASE_URL}${data.others_documents}`
//             : null,
//         });
//         setSelectedLocation({
//           districtId: response.data.data.district_id || null,
//           districtName: response.data.data.district || '',
//           upazilaId: response.data.data.upazila_id || null,
//           upazilaName: response.data.data.upazila || '',
//         });

//         // Set preview images
//         setNidPreview(
//           data.nid_certificate
//             ? `${PHOTO_BASE_URL}${data.nid_certificate}`
//             : null
//         );
//         setNomineePreview(
//           data.nominee_nid ? `${PHOTO_BASE_URL}${data.nominee_nid}` : null
//         );
//         setOtherDocsPreview(
//           data.others_documents
//             ? `${PHOTO_BASE_URL}${data.others_documents}`
//             : null
//         );
//       } catch (error) {
//         console.error('Error fetching profile data:', error);
//       }
//     };

//     fetchAffiliatorVerifyData();
//   }, []);

//   const handleSaveClick = async event => {
//     event.preventDefault();

//     if (!profileId) {
//       Swal.fire({
//         title: 'Error!',
//         text: 'Profile ID missing. Try reloading.',
//         icon: 'error',
//         confirmButtonText: 'Okay',
//         customClass: {
//           popup: 'w-72 h-auto p-3',
//           title: 'text-sm',
//           content: 'text-xs',
//           confirmButton:
//             'bg-DefaultColor text-white px-4 py-1 text-sm rounded-md',
//         },
//       });
//       return;
//     }

//     const formData = new FormData();

//     // Append only non-null values
//     if (profileData.full_name)
//       formData.append('full_name', profileData.full_name);
//     if (profileData.nid_certificate)
//       formData.append('nid_certificate', profileData.nid_certificate);
//     if (profileData.nominee_nid)
//       formData.append('nominee_nid', profileData.nominee_nid);
//     if (profileData.others_documents)
//       formData.append('others_documents', profileData.others_documents);

//     // Append selected location data
//     if (selectedLocation.districtId) {
//       formData.append('district_id', selectedLocation.districtId);
//     }
//     if (selectedLocation.upazilaId) {
//       formData.append('upazila_id', selectedLocation.upazilaId);
//     }

//     // Debugging: Log FormData entries
//     for (const [key, value] of formData.entries()) {
//       console.log(`${key}:`, value);
//     }

//     try {
//       const response = await apiClient.patch(
//         // '/affiliator/profile/update',
//         '/affiliator/profile/verification',
//         formData
//       );

//       console.log('verify response:', response.data);

//       Swal.fire({
//         title: 'Success!',
//         text: 'Verify profile successfully.',
//         icon: 'success',
//         confirmButtonText: 'Okay',
//         customClass: {
//           popup: 'w-72 h-auto p-3',
//           title: 'text-sm',
//           content: 'text-xs',
//           confirmButton:
//             'bg-DefaultColor text-white px-4 py-1 text-sm rounded-md',
//         },
//       });
//     } catch (error) {
//       console.error('Error updating profile:', error);

//       let errorMessage = 'Something went wrong!';
//       if (error.response && error.response.data) {
//         errorMessage = Object.values(error.response.data).flat().join('\n');
//       }

//       Swal.fire({
//         title: 'Update Failed!',
//         text: errorMessage,
//         icon: 'error',
//         confirmButtonText: 'Okay',
//         customClass: {
//           popup: 'w-72 h-auto p-3',
//           title: 'text-sm',
//           content: 'text-xs',
//           confirmButton:
//             'bg-DefaultColor text-white px-4 py-1 text-sm rounded-md',
//         },
//       });
//     }
//   };

//   const handleFileDrop = (e, name) => {
//     e.preventDefault();
//     const file = e.dataTransfer.files[0];
//     handleFileUpload(file, name);
//   };

//   const handleFileInput = (e, name) => {
//     const file = e.target.files[0];
//     handleFileUpload(file, name);
//   };

//   const handleFileUpload = (file, name) => {
//     if (!file) return;

//     // Validate file type
//     if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
//       Swal.fire({
//         title: 'Not Accept!',
//         text: 'Please upload a valid Image.',
//         icon: 'error',
//         customClass: {
//           popup: 'w-72 h-auto p-3',
//           title: 'text-sm',
//           content: 'text-xs',
//           confirmButton:
//             'bg-DefaultColor text-white px-4 py-1 text-sm rounded-md',
//         },
//       });
//       return;
//     }

//     // Create object URL for preview
//     const imageURL = URL.createObjectURL(file);

//     // Set preview state based on file type
//     if (name === 'nid_certificate') {
//       setNidPreview(imageURL);
//     } else if (name === 'nominee_nid') {
//       setNomineePreview(imageURL);
//     } else if (name === 'others_documents') {
//       setOtherDocsPreview(imageURL);
//     }

//     // Update profileData state
//     setProfileData(prevState => ({
//       ...prevState,
//       [name]: file,
//     }));

//     // Update file name
//     setFileNames(prevState => ({
//       ...prevState,
//       [name]: file.name,
//     }));
//   };

//   return (
//     <>
//       <div className="flex items-center gap-5 flex-col lg:flex-col justify-evenly md:px-20 px-2 md:py-24 md:mt-20 mt-24 py-10 md:text-xs text-xs max-w-5xl mx-auto">
//         <h1 className="font-bold text-xl">Verify your profile</h1>
//         {/* Left Side: Drag and Drop Form */}
//         <div className="w-full md:mb-0 mb-4 ">
//           <form
//             onSubmit={handleSaveClick}
//             className="space-y-4 md:p-6 p-3 bg-ProfileUpdateColor bg-opacity-40 rounded-lg shadow-md "
//           >
//             {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> */}
//             {/* <div className="space-y-4"> */}
//             {/* Name */}
//             <div className="flex flex-col md:flex-row items-center gap-3">
//               <label className="md:w-1/3 w-full text-profileUpdateTextColor font-medium">
//                 Name <span className="text-red-500">*</span>
//               </label>
//               <input
//                 type="text"
//                 name="full_name"
//                 value={profileData.full_name}
//                 placeholder="Enter your name"
//                 onChange={handleInputChange}
//                 required
//                 className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               />
//             </div>
//             <LocationInpc
//               onSelectLocation={handleLocationSelect}
//               selectedLocation={selectedLocation}
//             />
//             {/* </div> */}
//             {/* NID Drag and Drop */}
//             {[
//               { name: 'nid_certificate', label: 'NID', required: true },
//               { name: 'nominee_nid', label: 'Nominee NID', required: true },
//               {
//                 name: 'others_documents',
//                 label: 'Other Documents',
//                 required: false,
//               },
//             ].map(({ name, label, required }) => (
//               <div
//                 key={name}
//                 className="flex flex-col md:flex-row items-center gap-3"
//                 onDrop={e => handleFileDrop(e, name)}
//                 onDragOver={e => e.preventDefault()}
//               >
//                 <label
//                   className="md:w-1/3 w-full text-profileUpdateTextColor font-medium"
//                   htmlFor={name}
//                 >
//                   {label} {required && <span className="text-red-500">*</span>}
//                 </label>
//                 <div className="bg-white rounded-md p-1 w-full cursor-pointer border border-gray-300">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     name={name}
//                     id={name}
//                     onChange={e => handleFileInput(e, name)}
//                     className="hidden"
//                     required={required}
//                   />
//                   <label
//                     htmlFor={name}
//                     className="p-2 border-dashed border-2 border-gray-300 rounded text-center block"
//                   >
//                     <p>Drag & Drop or Click to Upload {label}</p>
//                     {fileNames[name] && (
//                       <p className="text-sm text-gray-600 mt-2">
//                         {fileNames[name]}
//                       </p>
//                     )}
//                   </label>
//                 </div>
//               </div>
//             ))}

//             {/* <div className="space-y-4">
//               </div> */}
//             {/* </div> */}

//             {/* Submit Button */}
//             <div className="flex justify-end">
//               <button
//                 type="submit"
//                 className="md:w-36 w-full p-2 bg-publishTenderBtn text-white rounded-md hover:bg-publishTenderBtnDark transition duration-300"
//               >
//                 Verify Profile
//               </button>
//             </div>
//           </form>
//         </div>

//         {/* Right Side: Preview Section */}
//         <div className="w-full p-2 rounded text-center flex justify-center items-center space-x-4">
//           {/* NID Preview */}
//           <div className="w-1/3">
//             <p className="font-semibold">NID</p>
//             {nidPreview ? (
//               <img
//                 src={nidPreview}
//                 alt="NID Preview"
//                 className="w-full h-40 object-cover mx-auto rounded-lg border"
//               />
//             ) : (
//               <div className="w-full h-40 flex items-center justify-center bg-gray-100 rounded-lg border">
//                 <p className="text-gray-500">No NID uploaded</p>
//               </div>
//             )}
//           </div>

//           {/* Nominee NID Preview */}
//           <div className="w-1/3">
//             <p className="font-semibold">Nominee NID</p>
//             {nomineePreview ? (
//               <img
//                 src={nomineePreview}
//                 alt="Nominee NID Preview"
//                 className="w-full h-40 object-cover mx-auto rounded-lg border"
//               />
//             ) : (
//               <div className="w-full h-40 flex items-center justify-center bg-gray-100 rounded-lg border">
//                 <p className="text-gray-500">No Nominee NID uploaded</p>
//               </div>
//             )}
//           </div>

//           {/* Other Documents Preview */}
//           <div className="w-1/3">
//             <p className="font-semibold">Other Documents</p>
//             {otherDocsPreview ? (
//               <img
//                 src={otherDocsPreview}
//                 alt="Other Documents Preview"
//                 className="w-full h-40 object-cover mx-auto rounded-lg border"
//               />
//             ) : (
//               <div className="w-full h-40 flex items-center justify-center bg-gray-100 rounded-lg border">
//                 <p className="text-gray-500">No Other Documents uploaded</p>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//       <Contact />
//     </>
//   );
// };

// export default VerifyProfile;
